import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives, createVNode as _createVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "project-details" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = { class: "horizontal-container" }
const _hoisted_4 = { class: "title-value" }
const _hoisted_5 = {
  key: 0,
  class: "kpis"
}
const _hoisted_6 = { class: "kpi__label" }
const _hoisted_7 = { class: "kpi__value" }
const _hoisted_8 = { class: "tabs-row" }
const _hoisted_9 = { key: 0 }

import { computed, nextTick, ref, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";
import {
  provideApolloClient,
  useMutation,
  useQuery,
} from "@vue/apollo-composable";

import enUK from "@/locales/project/en-UK.json";

import { GET_BASIC_PROJECT, GET_FULL_PROJECT } from "@/graphql/project/queries";
import {
  APPROVE_BUDGET,
  ARCHIVE_PROJECT,
  UNARCHIVE_PROJECT,
} from "@/graphql/project/mutations";

import HeaderBlock from "@/components/HeaderBlock.vue";
import EditProject from "@/components/CRUXEntity/EditProject.vue";
import BasicButton from "@/components/BasicButton.vue";

import { updatePaginatedQueryCache } from "@/helpers/cache/cache";
import ProjectsCacheInfoHolder from "@/helpers/project/ProjectsCacheInfoHolder";
import { removePaginatedEntity } from "@/helpers/cache/removeEntity";
import IconButton from "@/components/IconButton.vue";
import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import ConfirmModal from "@/components/ConfirmModal.vue";
import { formatPrice } from "@/helpers/utils";
import { useRoute } from "vue-router";
import { apolloClient } from "@/apollo.provider";
import useHasAccess from "@/composables/useHasAccess";
import useProjectNav from "@/composables/useProjectNav";
import Tabs from "@/components/Tabs.vue";
import { useIsWorkFullScreen } from "@/composables/useIsWorkFullScreen";

type MessageSchema = typeof enUK;


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectDetails',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});
const route = useRoute();
const toast = useToast();

const projectId = ref("");
onMounted(() => {
  projectId.value = props.id;
});

const { hasAccessToFinancialData, hasAdminAccess } = useHasAccess(projectId);

const {
  result: basicProjectResult,
  loading: basicProjectLoading,
  onError,
  onResult,
} = useQuery(GET_BASIC_PROJECT, () => ({
  id: props.id,
}));

const fullProjectLoading = ref(false);
const fullProjectResult = ref();
watch(
  hasAccessToFinancialData,
  (value, prevValue) => {
    if (value && !prevValue) {
      fullProjectLoading.value = true;
      provideApolloClient(apolloClient);
      const { onResult } = useQuery(GET_FULL_PROJECT, () => ({
        id: props.id,
      }));
      onResult((res) => {
        fullProjectResult.value = res.data;
      });
    }
  },
  { immediate: true },
);

const loading = computed(
  () => fullProjectLoading.value || basicProjectLoading.value,
);
const project = computed(
  () => fullProjectResult.value?.project ?? basicProjectResult.value?.project,
);

const budgetKpis = computed(() => {
  const p = fullProjectResult.value?.project;
  const total = p?.budget?.amount;
  const invoiced = p?.totalBilledNet;
  const performed = p?.performedAmount;

  return {
    total: {
      label: t("project.budget.label.total"),
      value: formatPrice(total),
    },
    ...(project.value?.internal
      ? {}
      : {
          invoiced: {
            label: t("project.budget.label.invoiced"),
            value: formatPrice(invoiced),
          },
        }),
    available: {
      label: t("project.budget.label.performed"),
      value: formatPrice(performed),
    },
  };
});
onResult(async (result) => {
  const clientId = result.data?.project?.client?.id;
  if (!hasAdminAccess.value)
    hasAdminAccess.value = await checkHasAccess(
      clientId,
      props.id,
      "admin",
      toast,
      t("errors.getUserDetails"),
    );
  await nextTick();
});

onError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.get"));
});

const {
  mutate: deactivateProject,
  onDone: onDoneDeactivate,
  onError: onErrorDeactivate,
  loading: loadingDeactivate,
} = useMutation(ARCHIVE_PROJECT, () => ({
  variables: {
    id: props.id,
  },
  update: (cache, { data }) => {
    const deactivateProjectResponse = data?.archiveProject;
    if (deactivateProjectResponse?.status === "ok") {
      updatePaginatedQueryCache(
        cache,
        deactivateProjectResponse.data,
        ProjectsCacheInfoHolder,
        removePaginatedEntity,
      );
    }
  },
}));

onDoneDeactivate(async (result) => {
  if (result.data?.archiveProject?.status === "ok") {
    toast.success(t("success.deactivate"));
  } else {
    const errorResponse = result.data?.archiveProject?.error;
    console.log(errorResponse);
    toast.error(t("errors.deactivate", { message: errorResponse }));
  }
});

onErrorDeactivate((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deactivate", { message: t("errors.internal") }));
});

const {
  mutate: unarchiveProject,
  onDone: onDoneReactivate,
  onError: onErrorReactivate,
  loading: loadingReactivate,
} = useMutation(UNARCHIVE_PROJECT, () => ({
  variables: {
    id: props.id,
  },
}));

onDoneReactivate(async (result) => {
  if (result.data?.unarchiveProject?.status === "ok") {
    toast.success(t("success.reactivate"));
  } else {
    const errorResponse = result.data?.unarchiveProject?.error;
    console.log(errorResponse);
    toast.error(t("errors.reactivate", { message: errorResponse }));
  }
});

onErrorReactivate((error) => {
  logErrorMessages(error);
  toast.error(t("errors.reactivate", { message: t("errors.internal") }));
});

const {
  mutate: approveBudget,
  onDone: onDoneApprove,
  onError: onErrorApprove,
} = useMutation(APPROVE_BUDGET, () => ({
  variables: {
    id: project.value?.budget?.id,
  },
}));

onDoneApprove(async (result) => {
  if (result.data?.approveBudget?.status === "ok") {
    isConfirmBudgetApproveModalVisible.value = false;
    toast.success(t("success.budgetapproval"));
  } else {
    const errorResponse = result.data?.approveBudget?.error;
    console.log(errorResponse);
    toast.error(t("errors.budgetapproval", { message: errorResponse }));
  }
});

onErrorApprove((error) => {
  logErrorMessages(error);
  toast.error(t("errors.budgetapproval", { message: t("errors.internal") }));
});

const isConfirmBudgetApproveModalVisible = ref(
  route.name === "ProjectBudgetDetails",
);
const closeConfirmBudgetApproveModal = () => {
  isConfirmBudgetApproveModalVisible.value = false;
};

const isEditProjectModalVisible = ref(false);
const showEditProjectModal = () => {
  isEditProjectModalVisible.value = true;
};
const closeEditProjectModal = () => {
  isEditProjectModalVisible.value = false;
};

const { tabs } = useProjectNav(projectId);

const { isWorkFullScreen } = useIsWorkFullScreen();

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (project.value)
        ? _withDirectives((_openBlock(), _createBlock(HeaderBlock, {
            key: 0,
            class: "header"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, _toDisplayString(project.value.name), 1),
                  (project.value.active && _unref(hasAdminAccess))
                    ? (_openBlock(), _createBlock(IconButton, {
                        key: 0,
                        iconName: "jam:pen-f",
                        hasBorders: false,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (showEditProjectModal()))
                      }))
                    : _createCommentVNode("", true),
                  (project.value && project.value.active && _unref(hasAdminAccess))
                    ? (_openBlock(), _createBlock(BasicButton, {
                        key: 1,
                        disabled: _unref(loadingDeactivate),
                        onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (_unref(deactivateProject)()), ["prevent"]))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("buttons.deactivate")), 1)
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  (project.value && !project.value.active && _unref(hasAdminAccess))
                    ? (_openBlock(), _createBlock(BasicButton, {
                        key: 2,
                        class: "reactivate",
                        disabled: _unref(loadingReactivate),
                        onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (_unref(unarchiveProject)()), ["prevent"]))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("buttons.reactivate")), 1)
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              (_unref(hasAccessToFinancialData) && loading.value)
                ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(budgetKpis.value, (kpi, key) => {
                      return (_openBlock(), _createElementBlock("li", {
                        class: "kpi",
                        key: key
                      }, [
                        _createElementVNode("span", _hoisted_6, _toDisplayString(kpi.label), 1),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(kpi.value), 1)
                      ]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 512)), [
            [_vShow, !_unref(isWorkFullScreen)]
          ])
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_8, [
        _createVNode(Tabs, {
          definitions: _unref(tabs),
          size: "sm",
          color: "dark"
        }, null, 8, ["definitions"])
      ], 512), [
        [_vShow, !_unref(isWorkFullScreen)]
      ]),
      _createVNode(_component_router_view),
      (
        project.value && project.value.active && _unref(hasAdminAccess) && isEditProjectModalVisible.value
      )
        ? (_openBlock(), _createBlock(EditProject, {
            key: 1,
            onClose: _cache[3] || (_cache[3] = ($event: any) => (closeEditProjectModal())),
            project: project.value
          }, null, 8, ["project"]))
        : _createCommentVNode("", true)
    ]),
    (isConfirmBudgetApproveModalVisible.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 0,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (closeConfirmBudgetApproveModal())),
          onNo: _cache[5] || (_cache[5] = ($event: any) => (closeConfirmBudgetApproveModal())),
          onYes: _cache[6] || (_cache[6] = ($event: any) => (_unref(approveBudget)())),
          question: _unref(t)('modal.budgetapproval.question')
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)("modal.budgetapproval.content.newBudget", {
          budget: _unref(formatPrice)(project.value.budget.amount),
        })), 1),
            (project.value.previouslyApprovedBudget)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(t)("modal.budgetapproval.content.previousBudget", {
          previousBudget: _unref(formatPrice)(project.value.previouslyApprovedBudget.amount),
        })), 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["question"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})